/**
 * Mobile Create Page - Refined
 *
 * Clean vertical form for blog generation with:
 * - Section icons and labels
 * - Topic input (auto-expanding)
 * - Chip-based keyword input with add button
 * - Horizontal scrolling tone selector
 * - Word count selector
 * - Sticky generate button at bottom
 * - Loading state with animation
 */

import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles,
  X,
  Plus,
  Loader2,
  CheckCircle,
  AlertCircle,
  Image,
  FileText,
  Globe,
  AlignLeft,
  Zap,
  Wand2,
  UploadCloud,
  ArrowLeft,
} from 'lucide-react';
import { marked } from 'marked';
import { useJobPoller, type GenerationConfig } from '../../hooks/useJobPoller';
import { MobileUploadPage } from './MobileUploadPage';
import { UserAvatarMenu } from './UserAvatarMenu';
import { CreditsBadge } from './CreditsBadge';

function renderMarkdown(content: string): string {
  if (!content) return '';
  const isHtml = content.trim().startsWith('<') || /<(p|div|h[1-6]|ul|ol|li)\b/i.test(content);
  if (isHtml) return content;
  try { return marked(content, { breaks: true, gfm: true }) as string; } catch { return content; }
}

const TONE_OPTIONS = [
  { value: 'professional', label: 'Professional' },
  { value: 'witty', label: 'Witty' },
  { value: 'academic', label: 'Academic' },
  { value: 'casual', label: 'Casual' },
  { value: 'persuasive', label: 'Persuasive' },
  { value: 'conversational', label: 'Conversational' },
];

const WORD_COUNT_OPTIONS = [
  { value: 800, label: 'Short', sublabel: '~800 words' },
  { value: 1200, label: 'Medium', sublabel: '~1,200 words' },
  { value: 1500, label: 'Standard', sublabel: '~1,500 words' },
  { value: 2000, label: 'Long', sublabel: '~2,000 words' },
];

interface MobileCreatePageProps {
  getAuthHeaders?: () => Promise<Record<string, string>>;
  onComplete?: () => void;
  onBack?: () => void;
  showBackButton?: boolean;
  userPlan?: 'free' | 'starter' | 'professional' | 'agency';
  credits?: number;
  onNavigateToPricing?: () => void;
  onSettingsClick?: () => void;
  onMFAClick?: () => void;
}

export const MobileCreatePage: React.FC<MobileCreatePageProps> = ({
  getAuthHeaders,
  userPlan = 'free',
  credits = 0,
  onNavigateToPricing,
  onSettingsClick,
  onMFAClick,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onComplete: _onComplete,
  onBack,
  showBackButton = false,
}) => {
  const [mode, setMode] = useState<'single' | 'bulk'>('single');
  const [topic, setTopic] = useState('');
  const [keywords, setKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [tone, setTone] = useState('professional');
  const [wordCount, setWordCount] = useState(1500);
  const [generateImages, setGenerateImages] = useState(true);

  // Show Bulk Upload page if in bulk mode
  if (mode === 'bulk') {
    return (
      <div className="relative">
        <MobileUploadPage onNavigateHome={() => setMode('single')} />
        {/* Back to Single Mode Button */}
        <button
          onClick={() => setMode('single')}
          className="absolute top-4 right-16 px-3 py-1.5 bg-slate-100 rounded-full text-slate-600 text-sm font-medium flex items-center gap-1 hover:bg-slate-200 transition-colors"
        >
          <Wand2 className="w-4 h-4" />
          Single
        </button>
      </div>
    );
  }

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const keywordInputRef = useRef<HTMLInputElement>(null);

  const { startJob, jobState, isPolling, error, resetState } = useJobPoller();
  const [cooldown, setCooldown] = useState(false);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.max(128, textareaRef.current.scrollHeight)}px`;
    }
  }, [topic]);

  const handleAddKeyword = () => {
    const trimmed = keywordInput.trim();
    if (trimmed && !keywords.includes(trimmed) && keywords.length < 5) {
      setKeywords([...keywords, trimmed]);
      setKeywordInput('');
    }
  };

  const handleKeywordKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddKeyword();
    } else if (e.key === 'Backspace' && !keywordInput && keywords.length > 0) {
      setKeywords(keywords.slice(0, -1));
    }
  };

  const handleRemoveKeyword = (keyword: string) => {
    setKeywords(keywords.filter((k) => k !== keyword));
  };

  const handleGenerate = async () => {
    if (!topic.trim() || cooldown) return;
    setCooldown(true);
    setTimeout(() => setCooldown(false), 10000);

    const config: GenerationConfig = {
      topic: topic.trim(),
      target_word_count: wordCount,
      generate_images: generateImages,
      tone,
      keywords: keywords.length > 0 ? keywords : undefined,
    };

    try {
      const headers = getAuthHeaders ? await getAuthHeaders() : {};
      startJob(config, headers);
    } catch (err) {
      console.error('Failed to get auth headers:', err);
    }
  };

  const handleNewGeneration = () => {
    resetState();
    setTopic('');
    setKeywords([]);
  };

  const getStepIcon = (step: string) => {
    if (step.toLowerCase().includes('image')) return <Image className="w-4 h-4" />;
    if (step.toLowerCase().includes('content') || step.toLowerCase().includes('writing'))
      return <FileText className="w-4 h-4" />;
    if (step.toLowerCase().includes('research')) return <Globe className="w-4 h-4" />;
    return <Sparkles className="w-4 h-4" />;
  };

  // Show completion/error state
  if (jobState?.status === 'completed') {
    return (
      <div className="px-4 py-6 pb-24">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-lg border border-green-200 p-6 text-center"
        >
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Spell Complete!
          </h2>
          <p className="text-gray-600 mb-6">
            Your content has been conjured successfully.
          </p>

          {jobState.content_preview && (
            <div
              className="mb-6 p-4 bg-gray-50 rounded-xl text-left text-sm text-gray-700 max-h-40 overflow-y-auto prose prose-sm"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(jobState.content_preview) }}
            />
          )}

          <div className="space-y-3">
            <button
              onClick={handleNewGeneration}
              className="w-full py-3 px-4 bg-purple-600 text-white rounded-xl font-medium touch-target"
            >
              Cast Another Spell
            </button>
            {jobState.result_url && (
              <a
                href={jobState.result_url}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full py-3 px-4 bg-green-600 text-white rounded-xl font-medium text-center touch-target"
              >
                View Generated Article
              </a>
            )}
          </div>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 py-6 pb-24">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-lg border border-red-200 p-6 text-center"
        >
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Spell Failed
          </h2>
          <p className="text-gray-600 mb-2">{error}</p>
          <button
            onClick={handleNewGeneration}
            className="mt-4 w-full py-3 px-4 bg-purple-600 text-white rounded-xl font-medium touch-target"
          >
            Try Again
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-full bg-slate-50">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-100 bg-white safe-area-inset-top">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {showBackButton && onBack && (
              <button
                onClick={onBack}
                className="p-2 -ml-2 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
            )}
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-purple-100 rounded-lg">
                <FileText className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-900">BlogCraft</h1>
                <p className="text-xs text-slate-500">AI Blog Writer</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <CreditsBadge
              credits={credits}
              plan={userPlan}
              compact={true}
              onUpgrade={onNavigateToPricing}
            />
            <button
              onClick={() => setMode('bulk')}
              className="px-3 py-1.5 bg-slate-100 rounded-full text-slate-600 text-sm font-medium flex items-center gap-1.5 hover:bg-slate-200 transition-colors"
            >
              <UploadCloud className="w-4 h-4" />
              Bulk
            </button>
            <UserAvatarMenu
              onSettingsClick={onSettingsClick}
              onMFAClick={onMFAClick}
            />
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-auto px-6 pt-6 pb-32">
        {/* Loading State */}
        {isPolling && jobState && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-purple-50 rounded-2xl p-5 border border-purple-100"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="relative">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                  {getStepIcon(jobState.current_step)}
                </div>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                  className="absolute inset-0 border-2 border-purple-600 border-t-transparent rounded-full"
                />
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900">Summoning...</p>
                <p className="text-sm text-purple-600">{jobState.current_step}</p>
              </div>
              <span className="text-lg font-bold text-purple-600">
                {jobState.progress_percent}%
              </span>
            </div>
            <div className="w-full bg-purple-200 rounded-full h-2 overflow-hidden">
              <motion.div
                className="h-full bg-purple-600 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${jobState.progress_percent}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
          </motion.div>
        )}

        {/* 1. Topic Input */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <AlignLeft className="w-4 h-4 text-purple-600" />
            <span className="text-slate-800 font-bold text-base">Topic / Title</span>
          </div>
          <textarea
            ref={textareaRef}
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            disabled={isPolling}
            className="w-full p-4 bg-white border border-slate-200 rounded-2xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none min-h-[128px] text-base text-slate-800 placeholder-slate-400 disabled:bg-slate-50 disabled:cursor-not-allowed"
            placeholder="e.g. The Future of AI in Healthcare..."
          />
        </div>

        {/* 2. Keywords (Chip Input) */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-4 h-4 text-yellow-500" />
            <span className="text-slate-800 font-bold text-base">Keywords</span>
            <span className="text-slate-400 text-xs">(Optional, max 5)</span>
          </div>

          {/* Input Row */}
          <div className="flex gap-3 mb-3">
            <input
              ref={keywordInputRef}
              type="text"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyDown={handleKeywordKeyDown}
              disabled={isPolling || keywords.length >= 5}
              className="flex-1 px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:bg-slate-50 disabled:cursor-not-allowed"
              placeholder="Add a keyword..."
            />
            <button
              type="button"
              onClick={handleAddKeyword}
              disabled={isPolling || keywords.length >= 5 || !keywordInput.trim()}
              className="w-12 bg-slate-900 rounded-xl flex items-center justify-center disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
            >
              <Plus className="w-6 h-6 text-white" />
            </button>
          </div>

          {/* Chips Container */}
          <div className="flex flex-wrap gap-2">
            <AnimatePresence>
              {keywords.map((keyword) => (
                <motion.span
                  key={keyword}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-purple-50 text-purple-700 rounded-lg text-sm font-medium border border-purple-100"
                >
                  {keyword}
                  <button
                    type="button"
                    onClick={() => handleRemoveKeyword(keyword)}
                    className="p-0.5 hover:bg-purple-100 rounded-full transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </motion.span>
              ))}
            </AnimatePresence>
            {keywords.length === 0 && (
              <span className="text-slate-400 text-sm italic py-2">No keywords added yet.</span>
            )}
          </div>
        </div>

        {/* 3. Tone Selector - Horizontal Scroll */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-purple-500" />
            <span className="text-slate-800 font-bold text-base">Tone of Voice</span>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-6 px-6 scrollbar-hide">
            {TONE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setTone(option.value)}
                disabled={isPolling}
                className={`flex-shrink-0 px-5 py-3 rounded-full text-sm font-semibold border transition-all touch-target ${
                  tone === option.value
                    ? 'bg-purple-600 border-purple-600 text-white'
                    : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                } disabled:opacity-50`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* 4. Word Count Selector */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="w-4 h-4 text-blue-500" />
            <span className="text-slate-800 font-bold text-base">Post Length</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {WORD_COUNT_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setWordCount(option.value)}
                disabled={isPolling}
                className={`p-4 rounded-xl text-left transition-all border ${
                  wordCount === option.value
                    ? 'bg-purple-600 border-purple-600 text-white shadow-md'
                    : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                } disabled:opacity-50`}
              >
                <p className="font-semibold">{option.label}</p>
                <p
                  className={`text-xs mt-0.5 ${
                    wordCount === option.value ? 'text-purple-200' : 'text-slate-400'
                  }`}
                >
                  {option.sublabel}
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* 5. Generate Images Toggle */}
        <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200 mb-8">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-100 rounded-lg">
              <Image className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="font-semibold text-slate-900">Generate Images</p>
              <p className="text-xs text-slate-500">AI-created visuals for your post</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setGenerateImages(!generateImages)}
            disabled={isPolling}
            className={`relative w-12 h-7 rounded-full transition-colors ${
              generateImages ? 'bg-purple-600' : 'bg-slate-300'
            } disabled:opacity-50`}
          >
            <motion.div
              animate={{ x: generateImages ? 22 : 2 }}
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              className="absolute top-1 w-5 h-5 bg-white rounded-full shadow"
            />
          </button>
        </div>
      </div>

      {/* Sticky Footer Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-100 p-5 pb-[calc(env(safe-area-inset-bottom)+1.25rem)] shadow-2xl z-20">
        <button
          onClick={handleGenerate}
          disabled={isPolling || !topic.trim() || cooldown}
          className={`w-full h-14 rounded-2xl text-white font-bold text-lg flex items-center justify-center gap-2 transition-all shadow-lg shadow-purple-500/30 touch-target ${
            isPolling || !topic.trim()
              ? 'bg-purple-400 cursor-not-allowed'
              : 'bg-purple-600 hover:bg-purple-700 active:scale-[0.98]'
          }`}
        >
          {isPolling ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Summoning...
            </>
          ) : (
            <>
              <Sparkles className="w-5 h-5" />
              Generate Content
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default MobileCreatePage;
